import Axios from 'axios';
import * as Sentry from '@sentry/vue';
import { isLocalEnvironment } from '@/utils/helpers';
import { userManager } from '../utils/auth';
import { visitorDataPromise } from '../utils/fingerprint';
import { sanitizeHybridCoursesAxiosErrorForTelemetry } from '@/utils/hybrid-courses-privacy';

let firstApiCallSucceeded = false;

export const baseAPI = Axios.create({
  baseURL: process.env.VUE_APP_PERFORMA_API + process.env.VUE_APP_API_BASE,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'P-CLIENT-VERSION': process.env.VUE_APP_BUILD_VERSION,
  },
});

const requestInterceptor = async function (config) {
  // Offline status check doesn't need a bearer token
  if (config.url === 'assets/offlineStatus') {
    return config;
  }

  const token = (await userManager.getUser()).access_token;
  config.headers['Authorization'] = 'Bearer ' + token;

  // Attempt to retrieve Browser Fingerprint
  try {
    const { visitorId, requestId } = await visitorDataPromise;
    const base64FingerPrint = btoa(JSON.stringify({ visitorId, requestId }));
    config.headers['Browser-Fingerprint'] = base64FingerPrint;
  } catch (e) {
    Sentry.captureException(e);
  }

  return config;
};

const responseInterceptor = function (error) {
  const { message } = error;
  const { status, data } = error.response || {};
  // Hybrid Courses failures are captured through a sanitized clone so
  // instructor/search values never reach Sentry; the untouched original is
  // still rejected to callers. Nonmatching endpoints return the same error.
  const telemetryError = sanitizeHybridCoursesAxiosErrorForTelemetry(error);
  const isLocalEnv = isLocalEnvironment();

  let pathToRedirect = '';

  if (/Network Error/.test(message)) {
    pathToRedirect = '/error?type=network';
    if (
      window.location.host !== process.env.VUE_APP_ENVIRONMENT_HOST.toString()
    ) {
      Sentry.captureException(new Error('Network Error: Wrong Domain'));
    } else {
      Sentry.captureException(
        new Error(
          `Network Error: ${
            window.navigator && window.navigator.onLine ? 'Online' : 'Offline'
          }`
        )
      );
    }
  } else if (status === 503) {
    const backOnlineAt = error.response.data?.backOnlineAt;
    if (backOnlineAt) {
      pathToRedirect = `${
        process.env.VUE_APP_PERFORMA_API
      }/Account/DowntimeMessage?backOnlineAt=${encodeURIComponent(
        backOnlineAt
      )}&returnUrl=${encodeURIComponent(window.location.origin)}`;
    } else {
      pathToRedirect = isLocalEnv
        ? process.env.VUE_APP_API_HOST + '/error?type=service'
        : '/error?type=service';
    }
  } else if (status === 401) {
    userManager.signinRedirect({ prompt: 'login' });
  } else if (status === 403 && data.errors) {
    const { code } = data.errors[0] || {};

    if (code === 'SESSION_NOT_CURRENT' || code === 'REQUEST_IS_BLACKLISTED') {
      userManager.signinRedirect({ prompt: 'login' });
    }
  } else {
    if (error.response) {
      try {
        const status = error.response.status;
        let endpoint = error.config.url;

        // Step 1: Remove query string (everything after ?)
        endpoint = endpoint.split('?')[0];

        // Step 2: Replace GUIDs with {guid}
        const guidRegex =
          /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
        endpoint = endpoint.replace(guidRegex, '{guid}');

        // Step 3: Replace integers with {id}
        const intRegex = /\b\d+\b/g;
        endpoint = endpoint.replace(intRegex, '{id}');

        // Step 4: Set custom message and fingerprint

        error.message = `Request failed with status code ${status} for ${endpoint}`;
        Sentry.withScope((scope) => {
          scope.setFingerprint([`http-error-${status}`, endpoint]);
          Sentry.captureException(telemetryError);
        });
      } catch (e) {
        Sentry.captureException(telemetryError);
      }
    } else {
      Sentry.captureException(telemetryError);
    }
  }

  if (pathToRedirect && !firstApiCallSucceeded) {
    window.location.href = pathToRedirect;
  } else {
    return Promise.reject(error);
  }
};

// request interceptors
baseAPI.interceptors.request.use(requestInterceptor, function (error) {
  Sentry.captureException(sanitizeHybridCoursesAxiosErrorForTelemetry(error));
  return Promise.reject(error);
});

baseAPI.interceptors.response.use(function (response) {
  firstApiCallSucceeded = true;
  return response;
}, responseInterceptor);
